import { defineStore } from 'pinia';
import axios from 'axios';
import { router } from '../../router.js';
import config from '../../config/config.js';
import { useAlertStore } from './alert.module';
import { useAuthStore } from './authentication.module';

const { baseURL } = config;

// Флаг предотвращает повторный вызов при параллельных 401-ответах
let is401Handling = false;

// Глобальный интерцептор — при 401 пробует обновить сессию через refresh-токен,
// и только если это не удалось — разлогинивает
axios.interceptors.response.use(
  response => response,
  async error => {
    if (error.response?.status === 401 && !is401Handling) {
      is401Handling = true;
      try {
        // Не пытаемся рефрешить сам запрос на refresh (избегаем петли)
        const isRefreshRequest = error.config?.url?.includes('/auth/refresh');
        if (!isRefreshRequest) {
          await axios.post(baseURL + '/auth/refresh', {}, { withCredentials: true });
          // Повторяем оригинальный запрос с теми же параметрами
          return axios(error.config);
        }
      } catch (refreshError) {
        // refresh тоже не прошёл: сессия истекла (401) или учётную запись
        // заблокировали (403) — причину объясняет ответ refresh, а не
        // исходного запроса
        const message = refreshError?.response?.data?.message
          || error.response.data?.message || 'Сессия истекла';
        await useAuthStore().logout();
        useAlertStore().error(message);
        router.push('/login').catch(e => e);
      } finally {
        is401Handling = false;
      }
    }
    return Promise.reject(error);
  }
);

export const useApiStore = defineStore('API', {
  actions: {
    async send(data) {
      const authStore = useAuthStore();
      if (!authStore.status?.loggedIn) {
        router.push('/login').catch(e => e);
        throw new Error('Not authenticated');
      }
      try {
        const response = await axios({
          method: data.method,
          url: baseURL + data.requestURL,
          data: data.requestData,
          withCredentials: true,
          responseType: data.responseType || 'json',
        });
        return data.responseType ? response : response.data;
      } catch (error) {
        const alertStore = useAlertStore();
        if (error.response) {
          if (error.response.status !== 401) {
            let errorData = error.response.data;
            if (errorData instanceof Blob) {
              if (errorData.type === 'application/json') {
                try { errorData = JSON.parse(await errorData.text()); } catch { errorData = {}; }
              } else {
                errorData = {};
              }
            }
            const statusMessages = { 504: 'Сервер не ответил вовремя (504)', 502: 'Сервер недоступен (502)', 503: 'Сервис недоступен (503)' };
            alertStore.error(errorData?.message || statusMessages[error.response.status] || error.response.statusText || `Ошибка ${error.response.status}`);
          }
        } else {
          // Сетевая ошибка / таймаут / CORS
          alertStore.error('Нет соединения с сервером');
        }
        throw error;
      }
    }
  }
});
